import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";

import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const PaymentVoucherContext = createContext();
export const usePaymentVoucher = () => useContext(PaymentVoucherContext);

export const PaymentVoucherProvider = ({ children }) => {
  const { token, isReady, logout } = useAuth();

  const [paymentVouchers, setPaymentVouchers] = useState([]);
  const [loading, setLoading] = useState(false);

  const mountedRef = useRef(true);

  const baseURL = BASE_URL.replace(/\/$/, "");
  const ENDPOINT = "/payment-vouchers";

  /* ---------------- MOUNT SAFETY ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetPaymentVouchers = (value) => {
    if (mountedRef.current) setPaymentVouchers(value);
  };

  /* ---------------- FETCH ---------------- */
  const fetchPaymentVouchers = useCallback(async () => {
    if (!token) return;

    setLoading(true);

    try {
      const res = await fetch(`${baseURL}${ENDPOINT}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(payload?.message || "Fetch failed");
      }

      const items = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];

      safeSetPaymentVouchers(items);

      return payload;
    } catch (err) {
      console.error("[PaymentVoucher] fetch error:", err);

      if (err.message?.toLowerCase().includes("token")) {
        logout?.();
      }

      safeSetPaymentVouchers([]);
      throw err;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token, baseURL, logout]);

  /* ---------------- CREATE ---------------- */
  const createPaymentVoucher = useCallback(
    async (voucherData) => {
      try {
        const isFormData =
          typeof FormData !== "undefined" &&
          voucherData instanceof FormData;

        const res = await fetch(`${baseURL}${ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            ...(isFormData
              ? {}
              : { "Content-Type": "application/json" }),
          },
          body: isFormData
            ? voucherData
            : JSON.stringify(voucherData),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(payload?.message || "Create failed");
        }

        await fetchPaymentVouchers();

        return payload;
      } catch (err) {
        console.error("[PaymentVoucher] create error:", err);
        throw err;
      }
    },
    [token, baseURL, fetchPaymentVouchers]
  );

  /* ---------------- OPTIONAL: UPDATE ---------------- */
  const updatePaymentVoucher = useCallback(
    async (id, data) => {
      try {
        const res = await fetch(
          `${baseURL}${ENDPOINT}/${id}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(data),
          }
        );

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(payload?.message || "Update failed");
        }

        await fetchPaymentVouchers();
        return payload;
      } catch (err) {
        console.error("[PaymentVoucher] update error:", err);
        throw err;
      }
    },
    [token, baseURL, fetchPaymentVouchers]
  );

  /* ---------------- OPTIONAL: DELETE ---------------- */
  const deletePaymentVoucher = useCallback(
    async (id) => {
      try {
        const res = await fetch(
          `${baseURL}${ENDPOINT}/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(payload?.message || "Delete failed");
        }

        safeSetPaymentVouchers((prev) =>
          prev.filter((v) => String(v.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[PaymentVoucher] delete error:", err);
        throw err;
      }
    },
    [token, baseURL]
  );

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    if (!isReady || !token) return;

    fetchPaymentVouchers().catch(() => {});
  }, [isReady, token, fetchPaymentVouchers]);

  return (
    <PaymentVoucherContext.Provider
      value={{
        paymentVouchers,
        loading,
        fetchPaymentVouchers,
        createPaymentVoucher,
        updatePaymentVoucher,
        deletePaymentVoucher,
        setPaymentVouchers,
      }}
    >
      {children}
    </PaymentVoucherContext.Provider>
  );
};